/**
 * edj-cart-drawer.js
 * Custom element <edj-cart-drawer> — slide-over panier global.
 * Lot 5 Batch C — EDJ Shopify theme
 *
 * Source de vérité : design-system/edj/project/cart-slideover.jsx
 *
 * Comportements :
 *   - Open : event 'cart:added' / 'cart:item-added' / [data-action="open-cart-drawer"]
 *   - Close : Escape / click backdrop / [data-action="close-cart-drawer"] / "Continuer"
 *   - Focus trap, return focus to trigger on close, body scroll lock
 *   - AJAX qty +/- + remove via /cart/change.js (sections=cart-drawer-content)
 *   - "+ Ajouter" reco card → /cart/add.js + refresh
 *   - Apply discount → /checkout?discount=CODE
 *   - Sync count badge header (event cart:updated)
 *   - Hydrate recommendations async via /recommendations/products?section_id=cart-drawer-recommendations
 *
 * Note sécurité : tous les fragments HTML injectés viennent de la Section
 * Rendering API Shopify (même origine, contrôlés par notre Liquid). Ils sont
 * parsés via DOMParser puis insérés via DOM nodes (pas innerHTML).
 */
(function () {
  'use strict';

  const SECTION_CONTENT = 'cart-drawer-content';
  const SECTION_RECOS = 'cart-drawer-recommendations';
  const ENDPOINT_CHANGE = '/cart/change.js';
  const ENDPOINT_ADD = '/cart/add.js';
  const ENDPOINT_RECOS = '/recommendations/products';
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  class EdjCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._isLoading = false;
      this._lastTrigger = null;
      this._recosHydrated = false;
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onCartAdded = this._onCartAdded.bind(this);
      this._onOpenTrigger = this._onOpenTrigger.bind(this);
      this._onDocFocus = this._onDocFocus.bind(this);
      this._onGlobalKeydown = this._onGlobalKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('submit', this._onSubmit);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('change', this._onChange);

      // Listen ALL cart:added variants (Lot 3 buy-box + bundle)
      document.addEventListener('cart:added', this._onCartAdded);
      document.addEventListener('cart:item-added', this._onCartAdded);

      // Listen open triggers globally (header cart icon, etc.)
      document.addEventListener('click', this._onOpenTrigger);

      // Escape global
      document.addEventListener('keydown', this._onGlobalKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('cart:added', this._onCartAdded);
      document.removeEventListener('cart:item-added', this._onCartAdded);
      document.removeEventListener('click', this._onOpenTrigger);
      document.removeEventListener('focusin', this._onDocFocus);
      document.removeEventListener('keydown', this._onGlobalKeydown);
    }

    _onGlobalKeydown(e) {
      if (e.key === 'Escape' && this._isOpen) this.close();
    }

    /* ---------------------- Open / Close ---------------------- */

    _onOpenTrigger(e) {
      const trigger = e.target.closest('[data-action="open-cart-drawer"]');
      if (!trigger) return;
      e.preventDefault();
      this._lastTrigger = trigger;
      this.open();
    }

    _onCartAdded() {
      // Refresh content first, then auto-open
      this._refreshContent().then(() => this.open());
    }

    open() {
      if (this._isOpen) return;
      this.removeAttribute('hidden');
      // Force reflow before adding state for animation
      void this.offsetHeight;
      this.setAttribute('data-state', 'open');
      this._isOpen = true;
      document.body.style.overflow = 'hidden';

      // Focus trap setup
      document.addEventListener('focusin', this._onDocFocus);

      // Move focus to close button
      setTimeout(() => {
        const closeBtn = this.querySelector('[data-action="close-cart-drawer"]');
        if (closeBtn) {
          closeBtn.focus();
        } else {
          const panel = this.querySelector('[data-cart-drawer-panel]');
          if (panel) panel.focus();
        }
      }, 50);

      // Hydrate recommendations once per session-open
      this._hydrateRecommendations();

      document.dispatchEvent(new CustomEvent('cart:drawer-open', { bubbles: true }));
    }

    close() {
      if (!this._isOpen) return;
      this.setAttribute('data-state', 'closed');
      this._isOpen = false;
      document.body.style.overflow = '';
      document.removeEventListener('focusin', this._onDocFocus);

      // Wait animation, then hide
      setTimeout(() => {
        if (!this._isOpen) {
          this.setAttribute('hidden', '');
          this.removeAttribute('data-state');
        }
      }, 400);

      // Return focus
      if (this._lastTrigger && document.body.contains(this._lastTrigger)) {
        this._lastTrigger.focus();
      }
      this._lastTrigger = null;

      document.dispatchEvent(new CustomEvent('cart:drawer-close', { bubbles: true }));
    }

    _onDocFocus(e) {
      if (!this._isOpen) return;
      const panel = this.querySelector('[data-cart-drawer-panel]');
      if (!panel) return;
      if (!panel.contains(e.target)) {
        const focusables = panel.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusables.length > 0) focusables[0].focus();
      }
    }

    /* ---------------------- Events ---------------------- */

    _onClick(e) {
      const closeBtn = e.target.closest('[data-action="close-cart-drawer"]');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      const action = e.target.closest('[data-action]');
      if (!action) return;
      const a = action.dataset.action;

      if (a === 'qty-decrement' || a === 'qty-increment') {
        e.preventDefault();
        this._handleQtyStep(action, a === 'qty-increment' ? 1 : -1);
        return;
      }
      if (a === 'remove-line') {
        e.preventDefault();
        const row = action.closest('[data-cart-line]');
        if (row) this._setLineQuantity(parseInt(row.dataset.cartLine, 10), 0);
        return;
      }
      if (a === 'reco-add') {
        e.preventDefault();
        const variantId = parseInt(action.dataset.variantId, 10);
        if (variantId) this._addToCart(variantId, 1, action);
        return;
      }
    }

    _onSubmit(e) {
      const form = e.target.closest('form[data-action="apply-discount"]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('[data-cart-discount-input]');
      const code = (input && input.value || '').trim();
      if (!code) {
        if (input) input.focus();
        return;
      }
      window.location.href = '/checkout?discount=' + encodeURIComponent(code);
    }

    _onKeydown(e) {
      if (e.key === 'Enter') {
        const input = e.target.closest('[data-cart-discount-input]');
        if (input) {
          e.preventDefault();
          const form = input.closest('form[data-action="apply-discount"]');
          if (form) form.requestSubmit();
          return;
        }
      }
      // Tab focus trap
      if (e.key === 'Tab' && this._isOpen) {
        const panel = this.querySelector('[data-cart-drawer-panel]');
        if (!panel) return;
        const focusables = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
          .filter((el) => el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    _onChange(e) {
      const input = e.target.closest('[data-cart-quantity-input]');
      if (!input) return;
      const row = input.closest('[data-cart-line]');
      if (!row) return;
      const newQty = Math.max(0, parseInt(input.value, 10) || 0);
      const lineIndex = parseInt(row.dataset.cartLine, 10);
      this._setLineQuantity(lineIndex, newQty);
    }

    _handleQtyStep(btn, delta) {
      const row = btn.closest('[data-cart-line]');
      if (!row) return;
      const input = row.querySelector('[data-cart-quantity-input]');
      const current = input ? (parseInt(input.value, 10) || 0) : 0;
      const next = Math.max(0, current + delta);
      const lineIndex = parseInt(row.dataset.cartLine, 10);
      this._setLineQuantity(lineIndex, next);
    }

    /* ---------------------- AJAX cart ops ---------------------- */

    async _setLineQuantity(lineIndex, quantity) {
      if (this._isLoading) return;
      if (!Number.isFinite(lineIndex) || lineIndex < 1) return;

      this._setLoading(true);
      try {
        const res = await fetch(ENDPOINT_CHANGE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: quantity,
            sections: SECTION_CONTENT,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error((data && data.description) || 'Erreur mise à jour panier');
        }

        if (data.sections && data.sections[SECTION_CONTENT]) {
          this._renderContent(data.sections[SECTION_CONTENT]);
        } else {
          await this._refreshContent();
        }
        this._emitCartUpdated(data);
      } catch (err) {
        console.error('[EDJ cart drawer] update error:', err);
      } finally {
        this._setLoading(false);
      }
    }

    async _addToCart(variantId, quantity, btn) {
      if (this._isLoading) return;
      const originalLabel = btn ? btn.textContent : null;
      this._setLoading(true);
      if (btn) {
        btn.disabled = true;
        btn.textContent = '…';
      }
      try {
        const res = await fetch(ENDPOINT_ADD, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error((data && data.description) || 'Erreur ajout au panier');
        }
        if (btn) btn.textContent = '✓';
        await this._refreshContent();
        this._emitCartUpdated(data);
        this._recosHydrated = false;
        this._hydrateRecommendations();
      } catch (err) {
        console.error('[EDJ cart drawer] add error:', err);
        if (btn) {
          btn.textContent = originalLabel || '+';
          btn.disabled = false;
        }
      } finally {
        this._setLoading(false);
      }
    }

    /* ---------------------- Render ---------------------- */

    async _refreshContent() {
      try {
        const url = '/?section_id=' + SECTION_CONTENT;
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) return;
        const html = await res.text();
        this._renderContent(html);
      } catch (err) {
        console.error('[EDJ cart drawer] refresh error:', err);
      }
    }

    _renderContent(html) {
      const target = this.querySelector('[data-cart-drawer-content]');
      if (!target) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newInner =
        doc.querySelector('.edj-cart-drawer__inner') ||
        doc.querySelector('[data-cart-drawer-inner]');
      if (newInner) {
        const currentInner = target.querySelector('.edj-cart-drawer__inner');
        if (currentInner) {
          currentInner.replaceWith(newInner);
        } else {
          clearChildren(target);
          target.appendChild(newInner);
        }
        this._recosHydrated = false;
        if (this._isOpen) this._hydrateRecommendations();
      }
    }

    /* ---------------------- Recommendations hydration ---------------------- */

    async _hydrateRecommendations() {
      if (this._recosHydrated) return;
      const recosBlock = this.querySelector('[data-cart-recos]');
      if (!recosBlock) return;
      const list = recosBlock.querySelector('[data-cart-recos-list]');
      if (!list) return;

      // If bundle_pack rendered cards in Liquid, skip hydration
      const existingCards = list.querySelectorAll('[data-reco-card]');
      if (existingCards.length > 0) {
        this._recosHydrated = true;
        return;
      }

      const anchorId = recosBlock.dataset.anchorId;
      const intent = recosBlock.dataset.intent || 'related';
      const limit = recosBlock.dataset.limit || '4';
      // Shopify recommendations API only accepts 'related' or 'complementary'
      const apiIntent = intent === 'complementary' ? 'complementary' : 'related';

      if (!anchorId) return;
      try {
        const url =
          ENDPOINT_RECOS +
          '?product_id=' + encodeURIComponent(anchorId) +
          '&intent=' + apiIntent +
          '&limit=' + encodeURIComponent(limit) +
          '&section_id=' + SECTION_RECOS;
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) {
          recosBlock.hidden = true;
          return;
        }
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-cart-recos-hydrated]');
        if (!inner) {
          recosBlock.hidden = true;
          return;
        }
        const cards = inner.querySelectorAll('[data-reco-card]');
        if (cards.length === 0) {
          recosBlock.hidden = true;
          return;
        }
        clearChildren(list);
        cards.forEach((c) => list.appendChild(c.cloneNode(true)));
        this._recosHydrated = true;
        // Iter 2026-05-08 follow-up — notify dots/drag-scroll component que de
        // nouvelles cards sont injectées (le MutationObserver le fait déjà mais
        // on dispatch en plus un event pour fiabilité cross-browser).
        document.dispatchEvent(new CustomEvent('cart:recos-hydrated', { bubbles: true }));
      } catch (err) {
        console.error('[EDJ cart drawer] recos hydrate error:', err);
        recosBlock.hidden = true;
      }
    }

    /* ---------------------- Helpers ---------------------- */

    _setLoading(state) {
      this._isLoading = state;
      this.dataset.loading = state ? 'true' : 'false';
    }

    _emitCartUpdated(data) {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: data,
        bubbles: true,
      }));
      this._syncHeaderBadge(data);
    }

    _syncHeaderBadge(data) {
      const count = (data && typeof data.item_count === 'number') ? data.item_count : null;
      if (count === null) return;

      // Two badges (desktop + mobile)
      document.querySelectorAll('.header__cart-badge').forEach((badge) => {
        if (count > 0) {
          badge.textContent = count;
          badge.setAttribute('aria-label', count + ' articles');
          badge.hidden = false;
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      });

      // If badge doesn't exist (cart was 0 at page load) and now > 0, inject one
      if (count > 0) {
        document.querySelectorAll('.header__cart').forEach((cartLink) => {
          if (!cartLink.querySelector('.header__cart-badge')) {
            const badge = document.createElement('span');
            badge.className = 'header__cart-badge';
            if (cartLink.classList.contains('header__cart--mobile')) {
              badge.className += ' header__cart-badge--mobile';
            }
            badge.textContent = count;
            badge.setAttribute('aria-label', count + ' articles');
            cartLink.appendChild(badge);
          }
        });
      }
    }
  }

  if (!customElements.get('edj-cart-drawer')) {
    customElements.define('edj-cart-drawer', EdjCartDrawer);
  }
})();
